<template>
  <div
    ref="wrapper"
    :style="wrapperStyle"
    @mouseenter="onPointerEnter"
    @mouseleave="onPointerLeave"
    @focus="onFocus"
    @blur="onBlur"
    @touchstart.prevent="onTouchStart"
    @touchend="onTouchEnd"
    tabindex="0"
    :aria-describedby="tooltipId"
  >
    <div :style="iconCircleStyle">
      <span :style="iconTextStyle">{{ iconChar }}</span>
    </div>

    <teleport to="body">
      <div
        v-show="visible"
        :id="tooltipId"
        ref="tooltip"
        class="tooltip-root"
        role="tooltip"
        :data-placement="currentPlacement"
        :style="computedTooltipStyle"
        @mouseenter="onTooltipEnter"
        @mouseleave="onTooltipLeave"
      >
        <div class="tooltip-content" v-html="tooltipText"></div>
        <div class="tooltip-arrow" :style="arrowStyle" />
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

/* ----------------------- props ----------------------- */
const props = defineProps({
  size: { type: Number, default: 2 },
  iconChar: { type: String, default: '?' },
  iconColor: { type: String, default: '#fff' },
  circleBg: { type: String, default: '#007bff' },

  tooltipText: { type: String, default: 'Подсказка' },
  tooltipBg: { type: String, default: '#333' },
  tooltipColor: { type: String, default: '#fff' },
  tooltipPadding: { type: String, default: '1rem' },

  borderRadius: { type: String, default: '50%' },

  offset: { type: Number, default: 8 },
  paddingViewport: { type: Number, default: 8 },
  placementPriority: {
    type: Array,
    default: () => ['top', 'bottom', 'right', 'left']
  },
  maxWidth: { type: String, default: 'calc(90vw - 16px)' },
  showDelay: { type: Number, default: 80 },
  hideDelay: { type: Number, default: 80 },
  idPrefix: { type: String, default: 'tt-' }
})

/* ----------------------- refs/state ----------------------- */
const wrapper = ref(null)
const tooltip = ref(null)
const visible = ref(false)
const isPointerOverTooltip = ref(false)
const currentPlacement = ref('top')
const pos = ref({ top: 0, left: 0 })
const arrowPos = ref({ left: '50%', top: null })
// ---------------------- FIX: use props.idPrefix here ----------------------
const tooltipId = ref(props.idPrefix + Math.random().toString(36).slice(2, 9))

let showTimer = null
let hideTimer = null
let touchTimer = null

/* ----------------------- styles for wrapper/icon ----------------------- */
const wrapperStyle = computed(() => ({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  cursor: 'pointer',
  outline: 'none'
}))

const iconCircleStyle = computed(() => ({
  width: props.size + 'rem',
  height: props.size + 'rem',
  borderRadius: props.borderRadius,
  background: props.circleBg,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}))

const iconTextStyle = computed(() => ({
  color: props.iconColor,
  fontSize: (props.size * 0.6) + 'rem',
  fontWeight: 'bold',
  userSelect: 'none'
}))

/* ----------------------- tooltip computed style ----------------------- */
const computedTooltipStyle = computed(() => {
  return {
    position: 'fixed',
    top: pos.value.top + 'px',
    left: pos.value.left + 'px',
    transform: currentPlacement.value === 'left' || currentPlacement.value === 'right'
      ? 'translateY(-50%)'
      : 'translateX(-50%)',
    background: props.tooltipBg,
    color: props.tooltipColor,
    padding: props.tooltipPadding,
    borderRadius: '0.25rem',
    whiteSpace: 'nowrap',
    fontSize: '0.875rem',
    zIndex: 2147483000,
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
    maxWidth: props.maxWidth,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    pointerEvents: 'auto',
    transition: 'opacity .12s ease, transform .12s ease',
    opacity: visible.value ? 1 : 0,
  }
})

const arrowStyle = computed(() => {
  if (currentPlacement.value === 'top' || currentPlacement.value === 'bottom') {
    return {
      left: arrowPos.value.left,
      top: null,
      transform: 'translateX(-50%)'
    }
  } else {
    return {
      top: arrowPos.value.top,
      left: null,
      transform: 'translateY(-50%)'
    }
  }
})

/* ----------------------- utility: debounce ----------------------- */
function debounce(fn, ms = 50) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), ms)
  }
}

/* ----------------------- core: calculate position ----------------------- */
async function recalcPosition() {
  if (!wrapper.value || !tooltip.value) return

  const w = wrapper.value.getBoundingClientRect()
  const t = tooltip.value.getBoundingClientRect()
  const winW = window.innerWidth
  const winH = window.innerHeight
  const pad = props.paddingViewport
  const offset = props.offset

  const space = {
    top: w.top - pad,
    bottom: winH - w.bottom - pad,
    left: w.left - pad,
    right: winW - w.right - pad
  }

  const fits = (placement) => {
    if (placement === 'top' || placement === 'bottom') {
      return (placement === 'top' ? space.top : space.bottom) >= (t.height + offset)
    } else {
      return (placement === 'left' ? space.left : space.right) >= (t.width + offset)
    }
  }

  let chosen = props.placementPriority.find(p => fits(p))
  if (!chosen) {
    const entries = Object.entries(space)
    entries.sort((a, b) => b[1] - a[1])
    chosen = entries[0][0]
  }

  let finalLeft = 0
  let finalTop = 0
  let arrowLeft = '50%'
  let arrowTop = '50%'

  if (chosen === 'top') {
    finalTop = Math.max(pad, w.top - t.height - offset)
    finalLeft = w.left + w.width / 2
    const half = t.width / 2
    if (finalLeft - half < pad) finalLeft = pad + half
    if (finalLeft + half > winW - pad) finalLeft = winW - pad - half
    const arrowPx = (w.left + w.width / 2) - (finalLeft - half)
    arrowLeft = Math.max(8, Math.min(t.width - 8, arrowPx)) + 'px'
  } else if (chosen === 'bottom') {
    finalTop = Math.min(winH - pad - t.height, w.bottom + offset)
    finalLeft = w.left + w.width / 2
    const half = t.width / 2
    if (finalLeft - half < pad) finalLeft = pad + half
    if (finalLeft + half > winW - pad) finalLeft = winW - pad - half
    const arrowPx = (w.left + w.width / 2) - (finalLeft - half)
    arrowLeft = Math.max(8, Math.min(t.width - 8, arrowPx)) + 'px'
  } else if (chosen === 'left') {
    finalLeft = Math.max(pad, w.left - t.width - offset)
    finalTop = w.top + w.height / 2
    const halfH = t.height / 2
    if (finalTop - halfH < pad) finalTop = pad + halfH
    if (finalTop + halfH > winH - pad) finalTop = winH - pad - halfH
    const arrowPx = (w.top + w.height / 2) - (finalTop - halfH)
    arrowTop = Math.max(8, Math.min(t.height - 8, arrowPx)) + 'px'
  } else if (chosen === 'right') {
    finalLeft = Math.min(winW - pad - t.width, w.right + offset)
    finalTop = w.top + w.height / 2
    const halfH = t.height / 2
    if (finalTop - halfH < pad) finalTop = pad + halfH
    if (finalTop + halfH > winH - pad) finalTop = winH - pad - halfH
    const arrowPx = (w.top + w.height / 2) - (finalTop - halfH)
    arrowTop = Math.max(8, Math.min(t.height - 8, arrowPx)) + 'px'
  }

  pos.value.left = Math.round(finalLeft)
  pos.value.top = Math.round(finalTop)
  currentPlacement.value = chosen
  if (chosen === 'top' || chosen === 'bottom') {
    arrowPos.value.left = arrowLeft
    arrowPos.value.top = null
  } else {
    arrowPos.value.top = arrowTop
    arrowPos.value.left = null
  }
}

/* ----------------------- event handlers & show/hide logic ----------------------- */
function showTooltip() {
  clearTimeout(hideTimer)
  if (visible.value) return
  showTimer = setTimeout(async () => {
    visible.value = true
    await nextTick()
    await recalcPosition()
  }, props.showDelay)
}

function hideTooltip() {
  clearTimeout(showTimer)
  if (!visible.value) return
  hideTimer = setTimeout(() => {
    if (!isPointerOverTooltip.value) visible.value = false
  }, props.hideDelay)
}

function onPointerEnter() { showTooltip() }
function onPointerLeave() { hideTooltip() }

function onTooltipEnter() {
  isPointerOverTooltip.value = true
  clearTimeout(hideTimer)
}
function onTooltipLeave() {
  isPointerOverTooltip.value = false
  hideTooltip()
}

function onFocus() { showTooltip() }
function onBlur() { hideTooltip() }

function onTouchStart() {
  clearTimeout(touchTimer)
  touchTimer = setTimeout(() => {
    showTooltip()
  }, 150)
}
function onTouchEnd() {
  clearTimeout(touchTimer)
}

/* ----------------------- react to resize/scroll/content changes ----------------------- */
const debouncedRecalc = debounce(async () => {
  if (!visible.value) return
  await nextTick()
  await recalcPosition()
}, 60)

let ro = null
let mo = null

onMounted(() => {
  window.addEventListener('resize', debouncedRecalc, { passive: true })
  window.addEventListener('scroll', debouncedRecalc, { passive: true })

  ro = new ResizeObserver(debouncedRecalc)
  if (wrapper.value) ro.observe(wrapper.value)

  mo = new MutationObserver(debouncedRecalc)
  mo.observe(document.body, { childList: true, subtree: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', debouncedRecalc)
  window.removeEventListener('scroll', debouncedRecalc)
  if (ro) ro.disconnect()
  if (mo) mo.disconnect()
  clearTimeout(showTimer)
  clearTimeout(hideTimer)
  clearTimeout(touchTimer)
})

watch(visible, async (v) => {
  if (v) {
    await nextTick()
    if (tooltip.value && ro) {
      try {
        ro.observe(tooltip.value)
      } catch (e) { /* ignore */ }
    }
    await recalcPosition()
  }
})
</script>

<style scoped>
.tooltip-root {
  pointer-events: auto;
  display: inline-block;
  will-change: transform, opacity;
  border-radius: 0.5rem;
  padding: 1rem;
}
.tooltip-content {
  display: inline-block;
  line-height: 1;
  text-align: center;
}
.tooltip-arrow {
  position: absolute;
  width: 1rem;
  height: 1rem;
  background: inherit;
  transform-origin: center;
  transform: rotate(45deg);
  box-shadow: inherit;
}
.tooltip-root[data-placement="top"] .tooltip-arrow { bottom: -5px; border-radius: 2px; }
.tooltip-root[data-placement="bottom"] .tooltip-arrow { top: -5px; border-radius: 2px; }
.tooltip-root[data-placement="left"] .tooltip-arrow { right: -5px; border-radius: 2px; }
.tooltip-root[data-placement="right"] .tooltip-arrow { left: -5px; border-radius: 2px; }
div[tabindex="0"]:focus { outline: 2px auto rgba(0,123,255,0.5); outline-offset: 3px; }

::v-deep(.instruction-list) {
  list-style: none;
  counter-reset: instruction-counter;
  padding-left: 2rem;
}

::v-deep(.instruction-list li) {
  counter-increment: instruction-counter;
  position: relative;
  margin-bottom: 0.5rem;
  font-size: 1rem;
  line-height: 1.6;
  text-align: left;
  color: var(--color-0);
  text-wrap: auto;
}

::v-deep(.instruction-list li::before) {
  content: counter(instruction-counter);
  position: absolute;
  left: -2rem;
  top: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background-color: var(--grey-0);
  color: var(--color-0);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.7rem;
  text-align: left;
}
</style>
